import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { Project, ProjectTask, Company, User } from '../api/supabaseEntities.js';
import { Button } from "../components/ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { 
  ArrowLeft, 
  Calendar, 
  Users, 
  Building2,
  Plus,
  ListFilter
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import ProjectKanban from '../components/projects/ProjectKanban.jsx';
import ProjectOverview from '../components/projects/ProjectOverview.jsx';
import ProjectTeam from '../components/projects/ProjectTeam.jsx';

export default function ViewProject() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id');

  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [company, setCompany] = useState(null);
  const [manager, setManager] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState('overview');

  useEffect(() => {
    if (projectId) {
      loadProjectData();
    }
  }, [projectId]);

  const loadProjectData = async () => {
    setIsLoading(true);
    try {
      const projectData = await Project.findById(projectId);
      setProject(projectData);

      // Carregar tarefas do projeto
      try {
        const tasksData = await ProjectTask.list({ project_id: projectId });
        setTasks(tasksData);
      } catch (error) {
        console.error('Error loading tasks:', error);
      }

      // Carregar dados da empresa
      try {
        const companies = await Company.list();
        if (projectData.client_id && companies.length > 0) {
          const foundCompany = companies.find(c => c.id === projectData.client_id);
          if (foundCompany) {
            setCompany(foundCompany);
          }
        }
      } catch (error) {
        console.error('Error loading company:', error);
      }

      // Carregar dados do gestor e equipe
      try {
        const users = await User.list();
        const internalUsers = users.filter(user => 
          user.role === 'admin' || user.role === 'support' || user.role === 'developer'
        );
        setTeamMembers(internalUsers);

        if (projectData.manager_id) {
          const managerData = users.find(u => u.id === projectData.manager_id);
          if (managerData) setManager(managerData);
        }

        if (projectData.team && projectData.team.length > 0) {
          const teamData = users.filter(u => projectData.team.includes(u.email));
          setTeamMembers(teamData);
        }
      } catch (error) {
        console.error('Error loading users:', error);
      }

    } catch (error) {
      console.error('Error loading project:', error);
      navigate(createPageUrl('Projects'));
    }
    setIsLoading(false);
  };

  const handleTaskUpdate = async (taskId, updates) => {
    try {
      await ProjectTask.update(taskId, updates);
      const updatedTasks = await ProjectTask.list({ project_id: projectId });
      setTasks(updatedTasks);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleCreateTask = async (taskData) => {
    try {
      await ProjectTask.create({
        ...taskData,
        project_id: projectId
      });
      const updatedTasks = await ProjectTask.list({ project_id: projectId });
      setTasks(updatedTasks);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // O erro está ocorrendo aqui. Vamos verificar se temos projectId e newStatus
      console.log("Atualizando projeto", projectId, "para status", newStatus);
      
      if (!projectId) {
        throw new Error("ID do projeto não encontrado");
      }
      
      await Project.update(projectId, { status: newStatus });
      await loadProjectData(); // Recarrega os dados do projeto
    } catch (error) {
      console.error('Erro ao atualizar status do projeto:', error);
      alert('Erro ao atualizar status do projeto. Por favor, tente novamente.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(createPageUrl('Projects'))}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">{project.name}</h1>
            <Badge className={
              project.status === 'planning' ? 'bg-purple-100 text-purple-800 border-purple-200' :
              project.status === 'in_progress' ? 'bg-blue-100 text-blue-800 border-blue-200' :
              project.status === 'paused' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
              'bg-green-100 text-green-800 border-green-200'
            }>
              {project.status === 'planning' ? 'Planejamento' :
               project.status === 'in_progress' ? 'Em Andamento' :
               project.status === 'paused' ? 'Pausado' :
               'Concluído'}
            </Badge>
          </div>
          {project.description && (
            <p className="text-muted-foreground mt-1">
              {project.description}
            </p>
          )}
        </div>
        <Button
          onClick={() => navigate(createPageUrl(`EditProject?id=${project.id}`))}
        >
          Editar Projeto
        </Button>
      </div>

      <Tabs value={currentView} onValueChange={setCurrentView} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="kanban">Kanban</TabsTrigger>
          <TabsTrigger value="team">Equipe</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <ProjectOverview 
            project={project}
            company={company}
            manager={manager}
            teamMembers={teamMembers}
            onStatusChange={handleStatusChange}
            tasks={tasks}
          />
        </TabsContent>
        <TabsContent value="kanban">
          <ProjectKanban 
            tasks={tasks}
            onTaskUpdate={handleTaskUpdate}
            onCreateTask={handleCreateTask}
            team={teamMembers}
          />
        </TabsContent>
        <TabsContent value="team">
          <ProjectTeam 
            project={project}
            manager={manager}
            teamMembers={teamMembers}
            internalUsers={teamMembers}
            onTeamUpdate={loadProjectData}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
